import type { ErrorEvent } from '@sentry/nextjs';

import type { DropReason, IgnoredDropHandler } from './drop-telemetry';
import {
  buildDropInfo,
  extractRoute,
  isLocalOrigin,
  resolveRequestHostname,
} from './drop-telemetry';

// Cap telemetry per drop key per window so a hot loop or a burst can't turn a
// swallowed error storm into a telemetry storm.
//
// The key is `reason|pattern`, not `pattern` alone (BAL-953). Keying on pattern
// would give all the reason-only branches ONE shared budget under the empty
// pattern, so a preview-env burst would exhaust the budget for a
// `next-not-found` burst in the same window and hide it — reintroducing the
// blind spot one level down. Per-pattern behaviour for the ignore-list is
// unchanged: its key is `ignore-pattern|<regex source>`.
//
// The browser caps per pageload, which is naturally short-lived. A server
// process is not: a warm Vercel lambda lives for hours, so a plain per-process
// counter would go permanently blind after the first burst. This is a TUMBLING
// window — the window opens at the first report and resets on the first report
// arriving 60s later — which bounds cost while keeping every later burst
// visible. Worst case is 40 reports across a boundary; that is fine here.
export const MAX_DROP_REPORTS_PER_PATTERN = 20;
export const DROP_REPORT_WINDOW_MS = 60_000;

/**
 * Build the per-process `beforeSend` drop reporter: a tumbling-window throttle
 * and a re-entrancy guard around `onIgnoredDrop`. The server config owns
 * `dropEvent`, which calls this, so dropping and attributing stay one statement.
 */
export function createIgnoredDropReporter({
  onIgnoredDrop,
  resolvedEnvironment,
}: {
  onIgnoredDrop?: IgnoredDropHandler;
  resolvedEnvironment: string;
}) {
  // Per-process rolling-window throttle state for onIgnoredDrop (BAL-891).
  const dropReportWindows = new Map<
    string,
    { count: number; windowStart: number }
  >();

  // Re-entrancy guard — see the browser half for the cycle it closes. The
  // window cap already terminates it; this stops it outright.
  let reporting = false;

  /**
   * Attribute one `beforeSend` drop. Called on EVERY `return null` below —
   * that completeness is the whole point of BAL-953, because Sentry's
   * `client_discard / before_send` counter counts every one of them and a
   * telemetry table covering a subset can never reconcile against it.
   *
   * There is no process-wide ceiling on top of the per-key window, and that is
   * deliberate: the key set is fixed (one per ignore pattern, plus one per
   * reason), so the worst case is already bounded at 20 × keys per minute. A
   * total ceiling would buy a modest volume reduction by silently truncating
   * whichever attribution arrived last — the failure this sink exists to remove.
   */
  function reportIgnoredDrop(args: {
    reason: DropReason;
    errorValue: string;
    event: ErrorEvent;
    pattern?: string;
  }) {
    if (!onIgnoredDrop) return;
    if (reporting) return;
    const { reason, errorValue, event, pattern = '' } = args;
    const key = `${reason}|${pattern}`;

    const now = Date.now();
    const window = dropReportWindows.get(key);
    let countInWindow: number;
    if (!window || now - window.windowStart >= DROP_REPORT_WINDOW_MS) {
      dropReportWindows.set(key, { count: 1, windowStart: now });
      countInWindow = 1;
    } else if (window.count >= MAX_DROP_REPORTS_PER_PATTERN) {
      return;
    } else {
      window.count += 1;
      countInWindow = window.count;
    }

    reporting = true;
    try {
      onIgnoredDrop(
        buildDropInfo({
          reason,
          pattern,
          errorValue,
          // BAL-1020: marks the last row this key may write in the current
          // 60s window. A floor for that window only — the next window
          // reopens the budget.
          keyExhausted: countInWindow >= MAX_DROP_REPORTS_PER_PATTERN,
          // ONE place decides whether the text ships, and it keys on the
          // ORIGIN rather than on which branch fired. Keying on the reason
          // instead would have covered only the `localhost` branch — and that
          // branch substring-matches the URL, so a dev machine serving on
          // `0.0.0.0`, `::1` or a LAN address falls through to a later reason
          // and would have shipped its text. Found by the BAL-953 security
          // review. Residual, stated rather than hidden: a tunnel host
          // (ngrok and friends) and an event with no request URL at all are
          // indistinguishable from production here, so those still ship the
          // same shape-redacted text production does.
          suppressMessage: isLocalOrigin(resolveRequestHostname(event)),
          route: extractRoute(event.request?.url, event.tags?.route),
          // `preview` DOES appear here since BAL-953. The wholesale
          // preview drop (BAL-791) still runs before every pattern match, so
          // an `ignore-pattern` row is still never tagged `preview` — but that
          // drop now reports itself under the `preview-environment` reason,
          // which is the only way the preview share of Sentry's discard
          // counter becomes visible at all.
          environment: resolvedEnvironment,
        }),
      );
    } catch {
      // Telemetry must never break the drop path.
    } finally {
      reporting = false;
    }
  }

  return reportIgnoredDrop;
}
